import { Button, Flex, InputNumber, Table, Tag, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type {
  MaintenanceContractIssue,
  MaintenanceSlaDraftLevel,
  MaintenanceSlaDraftPricingCombination,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import {
  addPricingCombination,
  removePricingCombination,
  withPricingCombination,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/utils';
import {
  durationPartsToMinutes,
  minutesToDurationParts,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/rules';
import { useMaintenanceSlaIssueHelp } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/hooks/useMaintenanceSlaIssueHelp';
import {
  addButtonStyle,
  durationContainerStyle,
  durationInputStyle,
  durationRowStyle,
  durationUnitStyle,
  optionTagStyle,
  pricingCombinationsHeaderStyle,
  pricingCombinationsHelpStyle,
  pricingCombinationsStyle,
  pricingCombinationsTableStyle,
} from './MaintenanceSlaPricingCombinationsEditor.style';

export interface MaintenanceSlaPricingCombinationsEditorProps {
  level: MaintenanceSlaDraftLevel;
  issues: readonly MaintenanceContractIssue[];
  onChange: (level: MaintenanceSlaDraftLevel) => void;
}

const DURATION_FIELDS: {
  key: keyof MaintenanceSlaDraftPricingCombination;
  labelKey: string;
}[] = [
  { key: 'takeChargeMinutes', labelKey: 'takeCharge' },
  { key: 'interventionMinutes', labelKey: 'interventionDuration' },
  { key: 'resolutionMinutes', labelKey: 'resolutionDuration' },
];

function DurationCell({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
}) {
  const { t } = useTranslation('sla');
  const parts =
    value == null
      ? { hours: null, minutes: null }
      : minutesToDurationParts(value);

  return (
    <div style={durationContainerStyle}>
      <div style={durationRowStyle}>
        <InputNumber
          min={0}
          precision={0}
          style={durationInputStyle}
          placeholder="0"
          value={parts.hours}
          onChange={(hours) =>
            onChange(durationPartsToMinutes(hours, parts.minutes))
          }
        />
        <span style={durationUnitStyle}>{t('oems.maintenance.hrs')}</span>
      </div>
      <div style={durationRowStyle}>
        <InputNumber
          min={0}
          max={59}
          precision={0}
          style={durationInputStyle}
          placeholder="0"
          value={parts.minutes}
          onChange={(minutes) =>
            onChange(durationPartsToMinutes(parts.hours, minutes))
          }
        />
        <span style={durationUnitStyle}>{t('oems.maintenance.min')}</span>
      </div>
    </div>
  );
}

export function MaintenanceSlaPricingCombinationsEditor({
  level,
  issues,
  onChange,
}: MaintenanceSlaPricingCombinationsEditorProps) {
  const { t } = useTranslation('sla');
  const help = useMaintenanceSlaIssueHelp(issues);

  const headerTitleStyle = {
    fontWeight: 600,
    color: '#475569',
    textShadow: 'none',
  };

  const bodyCellStyle = {
    padding: '12px 16px',
    verticalAlign: 'top' as const,
  };

  const headerCellStyle = {
    backgroundColor: '#f8fafc',
    boxShadow: 'none',
    borderBottom: '1px solid #e2e8f0',
  };

  const columns = [
    {
      title: (
        <span style={headerTitleStyle}>{t('oems.maintenance.option')}</span>
      ),
      key: 'option',
      width: 72,
      style: bodyCellStyle,
      onHeaderCell: () => ({ style: headerCellStyle }),
      render: (
        _: unknown,
        __: MaintenanceSlaDraftPricingCombination,
        index: number,
      ) => (
        <Tag style={optionTagStyle}>{String(index + 1).padStart(2, '0')}</Tag>
      ),
    },
    ...DURATION_FIELDS.map((field) => ({
      title: (
        <span style={headerTitleStyle}>
          {t(`oems.maintenance.${field.labelKey}`)}
        </span>
      ),
      key: field.key,
      width: 120,
      style: bodyCellStyle,
      onHeaderCell: () => ({ style: headerCellStyle }),
      render: (_: unknown, combo: MaintenanceSlaDraftPricingCombination) => (
        <DurationCell
          value={combo[field.key] as number | null}
          onChange={(next) =>
            onChange(
              withPricingCombination(level, combo.id, { [field.key]: next }),
            )
          }
        />
      ),
    })),
    {
      title: (
        <span style={headerTitleStyle}>{t('oems.maintenance.price')}</span>
      ),
      key: 'priceAmount',
      width: 160,
      style: bodyCellStyle,
      onHeaderCell: () => ({ style: headerCellStyle }),
      render: (_: unknown, combo: MaintenanceSlaDraftPricingCombination) => (
        <InputNumber
          min={1}
          precision={2}
          style={{ width: '100%' }}
          placeholder={t('oems.maintenance.price')}
          value={combo.priceAmount}
          onChange={(value) =>
            onChange(
              withPricingCombination(level, combo.id, { priceAmount: value }),
            )
          }
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      style: bodyCellStyle,
      onHeaderCell: () => ({ style: headerCellStyle }),
      render: (_: unknown, combo: MaintenanceSlaDraftPricingCombination) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onChange(removePricingCombination(level, combo.id))}
        />
      ),
    },
  ];

  const pricingHelp = help('pricingCombination', { levelCode: level.code });
  const priceHelp = help('pricingCombinationPrice', { levelCode: level.code });

  return (
    <div style={pricingCombinationsStyle}>
      <Flex
        justify="space-between"
        align="center"
        style={pricingCombinationsHeaderStyle}
      >
        <Typography.Text strong>
          {t('oems.maintenance.pricingCombinations')}
        </Typography.Text>
      </Flex>
      {pricingHelp || priceHelp ? (
        <Typography.Text type="danger" style={pricingCombinationsHelpStyle}>
          {pricingHelp ?? priceHelp}
        </Typography.Text>
      ) : null}
      <Table
        size="middle"
        pagination={false}
        rowKey="id"
        scroll={{ x: 780 }}
        dataSource={level.pricingCombinations}
        columns={columns}
        locale={{ emptyText: t('oems.maintenance.timersNone') }}
        style={pricingCombinationsTableStyle}
      />
      <Button
        type="link"
        icon={<PlusOutlined />}
        onClick={() => onChange(addPricingCombination(level))}
        style={addButtonStyle}
      >
        {t('oems.maintenance.addPricingCombination')}
      </Button>
    </div>
  );
}
